import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { getFunctions, httpsCallable } from "firebase/functions";
import { KHUSHI_COLORS, KHUSHI_FONTS } from "../../Khushi/theme";

const { gold, goldSoft, ink } = KHUSHI_COLORS;
const { display: fontDisplay, mono: fontMono, serif: fontSerif } = KHUSHI_FONTS;

type AuthUserRow = {
  uid: string;
  email: string | null;
  displayName: string | null;
  disabled: boolean;
  emailVerified: boolean;
  providerIds: string[];
  createdAt: string | null;
  lastSignInAt: string | null;
};

type ListUsersResponse = {
  users: AuthUserRow[];
  nextPageToken: string | null;
};

export function UsersTab() {
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [users, setUsers] = useState<AuthUserRow[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);

  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontFamily: fontDisplay,
        fontSize: "0.8rem",
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: gold,
        mb: 1.5,
      }}
    >
      {text}
    </Typography>
  );

  const loadUsers = async (pageToken?: string | null) => {
    setUsersError(null);
    setUsersLoading(true);
    try {
      const fn = httpsCallable<
        { maxResults?: number; pageToken?: string | null },
        ListUsersResponse
      >(getFunctions(), "listAuthUsers");

      const res = await fn({ maxResults: 50, pageToken: pageToken ?? null });
      const payload = res.data;
      setUsers((prev) =>
        pageToken ? [...prev, ...(payload.users ?? [])] : payload.users ?? [],
      );
      setNextToken(payload.nextPageToken ?? null);
    } catch (e: unknown) {
      setUsersError(
        e instanceof Error
          ? e.message
          : "Failed to load users. Ensure Functions are deployed and you're signed in as owner.",
      );
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (users.length === 0 && !usersLoading && !usersError) {
      loadUsers(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
          gap: 1,
        }}
      >
        {sectionTitle("Firebase Auth users")}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => loadUsers(null)}
            disabled={usersLoading}
            sx={{
              fontFamily: fontDisplay,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              borderColor: "rgba(212, 175, 55, 0.5)",
              color: goldSoft,
              "&:hover": { borderColor: gold },
            }}
          >
            Refresh
          </Button>
          {nextToken && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => loadUsers(nextToken)}
              disabled={usersLoading}
              sx={{
                fontFamily: fontDisplay,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                borderColor: "rgba(212, 175, 55, 0.5)",
                color: goldSoft,
                "&:hover": { borderColor: gold },
              }}
            >
              Load more
            </Button>
          )}
        </Box>
      </Box>

      {usersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {usersError}
        </Alert>
      )}

      {usersLoading && users.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} sx={{ color: gold }} />
        </Box>
      ) : (
        <TableContainer
          sx={{
            border: "1px solid rgba(212, 175, 55, 0.22)",
            borderRadius: 1,
            overflow: "scroll",
            width: "100%",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Email
                </TableCell>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Verified
                </TableCell>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Disabled
                </TableCell>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Providers
                </TableCell>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Created
                </TableCell>
                <TableCell sx={{ color: goldSoft, fontFamily: fontMono }}>
                  Last sign-in
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.uid}>
                  <TableCell sx={{ color: ink, fontFamily: fontSerif }}>
                    {u.email ?? "—"}
                  </TableCell>
                  <TableCell sx={{ color: ink, fontFamily: fontMono }}>
                    {u.emailVerified ? "yes" : "no"}
                  </TableCell>
                  <TableCell sx={{ color: ink, fontFamily: fontMono }}>
                    {u.disabled ? "yes" : "no"}
                  </TableCell>
                  <TableCell sx={{ color: ink, fontFamily: fontMono }}>
                    {(u.providerIds ?? []).join(", ") || "—"}
                  </TableCell>
                  <TableCell sx={{ color: ink, fontFamily: fontMono }}>
                    {u.createdAt ?? "—"}
                  </TableCell>
                  <TableCell sx={{ color: ink, fontFamily: fontMono }}>
                    {u.lastSignInAt ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && !usersLoading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: goldSoft }}>
                    No users returned.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {usersLoading && users.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
          <CircularProgress size={18} sx={{ color: gold }} />
        </Box>
      )}
    </>
  );
}

